import { Typography } from '@euler/app/components/typography';
import { OrderSearchResultList } from '@euler/app/flows/search/components/OrderSearchResultList';
import {
  useOrderSearchContext,
  useOrderSearchForm,
} from '@euler/app/flows/search/context';
import { useSearchOrders } from '@euler/app/flows/search/functions/useSearchOrders';
import { Center } from '@euler/components/Center';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useAdaptiveContainerWidth } from '@euler/functions/useAdaptiveContainerWidth';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { SearchOrderParams } from '@euler/model/order/SearchOrderParams';
import { SearchOrderResultItem } from '@euler/model/order/SearchOrderResultItem';
import { memo, useMemo } from 'react';
import { KeyboardAvoidingView, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export const OrderSearchResultsPage = wrapNavigatorScreen(
  (_: { onPressResultItem?: (item: SearchOrderResultItem) => void }) => {
    const width = useAdaptiveContainerWidth();
    const { form } = useOrderSearchForm();

    const params = useMemo<SearchOrderParams>(
      () => ({
        orderNo: form.orderNo.value?.trim(),
        licensePlateNo: form.licensePlateNo.value?.trim(),
        vin: form.vin.value?.trim(),
        serviceAgentId: form.serviceAgent.value?.id,
        mileage: form.mileage.value,
        vehicleBrandName: form.vehicleBrandName.value?.trim(),
        status: form.status.value,
        vehicleModel: form.vehicleModel.value?.trim(),
        vehicleFuelType: form.vehicleFuelType.value,
        createdAt: form.createdAt.value,
        finishedAt: form.finishedAt.value,
        tags: form.tags.value,
        keyword: form.keyword.value?.trim(),
        customerName: form.customerName.value?.trim(),
        customerPhone: form.customerPhone.value?.trim(),
      }),
      [form],
    );

    const {
      data,
      status: { hasMore, isLoading, error },
      fetchSinceLastError,
      onScrollToBottom,
    } = useSearchOrders(params);

    const { openOrderSearchResultItem } = useOrderSearchContext();

    const styles = useStyles();

    return (
      <KeyboardAvoidingView behavior="padding" style={styles.flexContainer}>
        <OrderSearchResultList
          showFinishDate
          width={width}
          data={data}
          isLoading={isLoading}
          error={error}
          hasMore={hasMore}
          ListEmptyComponent={<EmptyPlaceholder isLoading={isLoading} />}
          onRetry={fetchSinceLastError}
          onItemPress={openOrderSearchResultItem}
          onScrollToBottom={onScrollToBottom}
          css={`
            flex: 1;
          `}
        />
      </KeyboardAvoidingView>
    );
  },
  {
    title: '搜索结果',
  },
);

const EmptyPlaceholder = memo(({ isLoading }: { isLoading?: boolean }) => {
  const { rpx } = useResponsiveContext();
  if (isLoading) return null;
  return (
    <Center
      css={`
        padding: ${rpx(32)}px;
        height: ${rpx(200)}px;
      `}
    >
      <Typography.Text light color="#555">
        未找到相关订单
      </Typography.Text>
    </Center>
  );
});

const useStyles = () => {
  const { rpx } = useResponsiveContext();
  const { bottom } = useSafeAreaInsets();
  return useMemo(
    () =>
      StyleSheet.create({
        flexContainer: {
          flex: 1,
        },
        listContainer: {
          flexGrow: 1,
          minHeight: 0,
          paddingBottom: Math.max(bottom, rpx(15)),
          paddingTop: rpx(10),
          paddingLeft: rpx(15),
          paddingRight: rpx(15),
        },
      }),
    [bottom, rpx],
  );
};
